import React, { useMemo, useState } from "react";
import { Button, InputNumber } from "antd";
import { COLORS } from "../../../consts";

const graphColors = COLORS.graphColors;
const PercentageCalculator = ({
  siteMetadata,
  currentGreenhouse,
  currentPlot,
  minSize,
  maxSize,
  setMinSize,
  setMaxSize,
  setShowPercentage,
  showPercentage,
  filteredGraphData,
  isImperial = false,
}) => {
  const unit = isImperial ? "in" : "cm";
  const [totalPercentages, setTotalPercentages] = useState([]);

  const totalActualHeadCount = useMemo(() => {
    if (siteMetadata && currentGreenhouse && currentPlot) {
      return siteMetadata?.[currentGreenhouse.name]?.[currentPlot.name]?.["actual_headcount"];
    }
    return null;
  }, [siteMetadata, currentGreenhouse, currentPlot]);

  const handleClear = () => {
    setShowPercentage(false);
    setMinSize(0);
    setMaxSize(isImperial ? Number(25/2.5).toFixed(2) : 25);
  };

  const calculateTotalPercentages = () => {
    if (!filteredGraphData.length) return;
    setShowPercentage(false);

    const totals = filteredGraphData.map((dataset) => {
      let minIndex, maxIndex, histData;

      if (isImperial) {
        minIndex = Math.floor(minSize * 2.5);
        maxIndex = Math.floor(maxSize * 2.5);
        histData = dataset.cmBasedData || dataset.data;
      } else {
        // Metric: use cmBasedData for consistent 1cm binning
        minIndex = minSize;
        maxIndex = maxSize;
        histData = dataset.cmBasedData || dataset.data;
      }

      const relevantData = histData.filter(
        (value, index) => index >= minIndex && index <= maxIndex,
      );
      const total = relevantData.reduce((acc, val) => acc + val, 0).toFixed(2);
      const totalCount = Math.floor((total / 100) * totalActualHeadCount);
      return { date: dataset.label, percentage: total, totalCount: totalCount };
    });

    setTotalPercentages(totals);
    setShowPercentage(true);
  };

  return (
    <div className="percentage-calculator w-full ">
      <h2 className="font-bold text-lg mb-3">In-Spec Broccoli Calculator</h2>
      <h3 className="font-bold mb-2">Stand Count : {totalActualHeadCount}</h3>

      <div className="flex flex-wrap items-center justify-between mb-3 sm:flex-col md:flex-row lg:mt-0 mt-4">
        <div className="flex items-center sm:w-full mb-4">
          <span>Minimum Size ({unit}): </span>
          <InputNumber
            min={0}
            max={isImperial ? Number(25 / 2.5).toFixed(2) : 25}
            step={isImperial ? Number(1 / 2.5).toFixed(2) : 1}
            precision={isImperial ? 2 : 0}
            value={minSize}
            onChange={setMinSize}
            className="mx-3 my-0 w-[80px]"
          />
        </div>

        <div className="flex items-center sm:w-full mb-4">
          <span>Maximum Size ({unit}): </span>
          <InputNumber
            min={0}
            max={isImperial ? Number(25 / 2.5).toFixed(2) : 25}
            step={isImperial ? Number(1 / 2.5).toFixed(2) : 1}
            precision={isImperial ? 2 : 0}
            value={maxSize}
            onChange={setMaxSize}
            className="mx-3 my-0 w-[80px]"
          />
        </div>

        <div className="ml-10 flex gap-3 sm:w-full sm:ml-0 sm:justify-start">
          <Button type="primary" onClick={calculateTotalPercentages}>
            Calculate
          </Button>
          {showPercentage && (
            <Button type="default" onClick={handleClear}>
              Clear
            </Button>
          )}
        </div>
      </div>

      {showPercentage && (
        <div className="mt-5">
          <h3 className="font-bold mb-2">Total In-Spec Broccoli by Date Range:</h3>
          {totalPercentages.map((total, index) => (
            <div key={total.date} className="flex items-center mb-2">
              <div
                className="w-3 h-3 mr-2"
                style={{
                  backgroundColor: graphColors[index % graphColors.length],
                }}
              ></div>
              <span>
                {total.date}: {total.totalCount} ({total.percentage}%)
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export { PercentageCalculator };
